'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import {
  trackWalkthroughStarted,
  trackWalkthroughStep,
  trackWalkthroughCompleted,
  trackWalkthroughAbandoned,
  trackScholarClicked,
  trackScholarSelected,
  trackSignupFromWalkthrough,
  computeCompletionRate,
} from '@/lib/analytics/seniorsTracking';

// ─── Types ────────────────────────────────────────────────────────────────────
type WalkthroughStep = 'intro' | 'directory' | 'scholar-profile' | 'ask-flow' | 'select-scholar' | 'done';

interface Scholar {
  id: string;
  name: string;
  title: string;
  flag: string;
  specialisation: string[];
  responseTime: string;
  questionsAnswered: number;
  available: boolean;
  bio: string;
}

const DEMO_SCHOLARS: Scholar[] = [
  {
    id: 'sheikh-ibrahim-al-husseini',
    name: 'Sheikh Ibrahim Al-Husseini',
    title: 'Islamic Scholar & Fiqh Specialist',
    flag: '🇰🇪',
    specialisation: ['Fiqh', 'Ibadah', 'Purification'],
    responseTime: '24–48 hours',
    questionsAnswered: 1240,
    available: true,
    bio: 'Over 25 years of experience in Islamic jurisprudence, specialising in matters of worship and purification for the elderly.',
  },
  {
    id: 'sheikh-omar-faruq',
    name: 'Sheikh Omar Faruq',
    title: 'Quran & Tafsir Scholar',
    flag: '🇸🇴',
    specialisation: ['Quran', 'Tafsir', 'Dhikr'],
    responseTime: '12–24 hours',
    questionsAnswered: 890,
    available: true,
    bio: 'Hafidh of the Quran with deep expertise in Tafsir and the spiritual dimensions of daily worship.',
  },
  {
    id: 'sheikh-yusuf-al-makki',
    name: 'Sheikh Yusuf Al-Makki',
    title: 'Aqeedah & Sunnah Scholar',
    flag: '🇲🇾',
    specialisation: ['Aqeedah', 'Hadith', 'Sunnah'],
    responseTime: '24–48 hours',
    questionsAnswered: 670,
    available: false,
    bio: 'Graduate of Madinah University, dedicated to guiding seniors in strengthening their faith through authentic Sunnah.',
  },
];

const STEP_ORDER: WalkthroughStep[] = ['intro', 'directory', 'scholar-profile', 'ask-flow', 'select-scholar', 'done'];

const STEP_META: Record<WalkthroughStep, { label: string; emoji: string }> = {
  intro: { label: 'Welcome', emoji: '🌙' },
  directory: { label: 'Scholar Directory', emoji: '📚' },
  'scholar-profile': { label: 'Scholar Profile', emoji: '👤' },
  'ask-flow': { label: 'Submit a Question', emoji: '✍️' },
  'select-scholar': { label: 'Choose Your Scholar', emoji: '🤝' },
  done: { label: 'Get Started', emoji: '🚀' },
};

// ─── Step Components ──────────────────────────────────────────────────────────

function IntroStep({ onNext }: { onNext: () => void }) {
  return (
    <div className="flex flex-col items-center text-center px-2">
      <div className="w-20 h-20 rounded-2xl gradient-teal flex items-center justify-center text-4xl shadow-prayer mb-6">
        🌙
      </div>
      <h3 className="text-2xl font-800 text-foreground mb-3 leading-tight">
        Assalamu Alaykum,<br />
        <span className="text-primary">dear Senior</span>
      </h3>
      <p className="text-muted-foreground text-sm leading-relaxed max-w-xs mb-6">
        Rafeeq connects you with verified Islamic scholars who answer your personal questions with care, wisdom, and respect for your age and experience.
      </p>
      <div className="w-full bg-muted/50 border border-border rounded-2xl p-4 mb-6 text-left space-y-3">
        {[
          { icon: '📚', text: 'Browse a directory of verified scholars' },
          { icon: '✍️', text: 'Submit your question privately' },
          { icon: '📩', text: 'Receive a personal, thoughtful answer' },
        ].map((item) => (
          <div key={item.text} className="flex items-center gap-3">
            <span className="text-xl">{item.icon}</span>
            <span className="text-sm text-foreground">{item.text}</span>
          </div>
        ))}
      </div>
      <button
        onClick={onNext}
        className="w-full py-3.5 rounded-xl gradient-teal text-white font-700 text-sm shadow-prayer hover:opacity-90 transition-opacity"
      >
        Show me how it works →
      </button>
    </div>
  );
}

function DirectoryStep({ onNext, onScholarClick }: { onNext: () => void; onScholarClick: (scholarId: string) => void }) {
  const [filter, setFilter] = useState('All');
  const tags = ['All', 'Fiqh', 'Quran', 'Aqeedah', 'Dhikr'];

  const filtered = filter === 'All'
    ? DEMO_SCHOLARS
    : DEMO_SCHOLARS.filter((s) => s.specialisation.includes(filter));

  return (
    <div className="flex flex-col">
      <div className="mb-4">
        <p className="text-xs font-600 text-primary uppercase tracking-widest mb-1">Step 1 of 3</p>
        <h3 className="text-xl font-800 text-foreground mb-1">Scholar Directory</h3>
        <p className="text-sm text-muted-foreground">Browse verified scholars by specialisation. Each scholar is reviewed by our admin team.</p>
      </div>

      {/* Filter chips */}
      <div className="flex gap-2 flex-wrap mb-4">
        {tags.map((tag) => (
          <button
            key={tag}
            onClick={() => setFilter(tag)}
            className={`px-3 py-1 rounded-full text-xs font-600 border transition-colors ${
              filter === tag
                ? 'bg-primary text-white border-primary' :'bg-muted text-muted-foreground border-border hover:border-primary/40'
            }`}
          >
            {tag}
          </button>
        ))}
      </div>

      {/* Scholar cards */}
      <div className="space-y-3 mb-5">
        {filtered.map((scholar) => (
          <div
            key={scholar.id}
            onClick={() => onScholarClick(scholar.id)}
            className="flex items-start gap-3 bg-card border border-border rounded-xl p-3 hover:border-primary/30 transition-colors cursor-pointer"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-xl flex-shrink-0">
              {scholar.flag}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-sm font-700 text-foreground truncate">{scholar.name}</span>
                {scholar.available && (
                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-400" />
                )}
              </div>
              <p className="text-xs text-muted-foreground mb-1.5">{scholar.title}</p>
              <div className="flex flex-wrap gap-1">
                {scholar.specialisation.map((s) => (
                  <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-500">
                    {s}
                  </span>
                ))}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-xs text-muted-foreground">{scholar.questionsAnswered}</p>
              <p className="text-[10px] text-muted-foreground/70">answers</p>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={onNext}
        className="w-full py-3 rounded-xl gradient-teal text-white font-700 text-sm shadow-prayer hover:opacity-90 transition-opacity"
      >
        View a scholar's profile →
      </button>
    </div>
  );
}

function ScholarProfileStep({ onNext }: { onNext: () => void }) {
  const scholar = DEMO_SCHOLARS[0];

  return (
    <div className="flex flex-col">
      <div className="mb-4">
        <p className="text-xs font-600 text-primary uppercase tracking-widest mb-1">Step 1 of 3 — Detail View</p>
        <h3 className="text-xl font-800 text-foreground mb-1">Scholar Profile</h3>
        <p className="text-sm text-muted-foreground">Each scholar has a full profile so you can choose the right fit for your question.</p>
      </div>

      {/* Profile card */}
      <div className="bg-card border border-border rounded-2xl p-4 mb-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-3xl flex-shrink-0">
            {scholar.flag}
          </div>
          <div>
            <h4 className="font-800 text-foreground text-base">{scholar.name}</h4>
            <p className="text-xs text-muted-foreground">{scholar.title}</p>
            <div className="flex items-center gap-1.5 mt-1">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              <span className="text-xs text-emerald-500 font-600">Available now</span>
            </div>
          </div>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed mb-4">{scholar.bio}</p>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-muted/50 rounded-xl p-3 text-center">
            <p className="text-lg font-800 text-foreground">{scholar.questionsAnswered.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Questions answered</p>
          </div>
          <div className="bg-muted/50 rounded-xl p-3 text-center">
            <p className="text-lg font-800 text-foreground">{scholar.responseTime}</p>
            <p className="text-xs text-muted-foreground">Response time</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {scholar.specialisation.map((s) => (
            <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary font-600 border border-primary/20">
              {s}
            </span>
          ))}
        </div>
      </div>

      <button
        onClick={onNext}
        className="w-full py-3 rounded-xl gradient-teal text-white font-700 text-sm shadow-prayer hover:opacity-90 transition-opacity"
      >
        See how to submit a question →
      </button>
    </div>
  );
}

function AskFlowStep({ onNext }: { onNext: () => void }) {
  const [questionText, setQuestionText] = useState('');
  const [charCount, setCharCount] = useState(0);
  const MAX_CHARS = 500;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value.slice(0, MAX_CHARS);
    setQuestionText(val);
    setCharCount(val.length);
  };

  return (
    <div className="flex flex-col">
      <div className="mb-4">
        <p className="text-xs font-600 text-primary uppercase tracking-widest mb-1">Step 2 of 3</p>
        <h3 className="text-xl font-800 text-foreground mb-1">Submit a Question</h3>
        <p className="text-sm text-muted-foreground">Type your question in your own words. Your question is private and only shared with your chosen scholar.</p>
      </div>

      {/* Scholar badge */}
      <div className="flex items-center gap-2.5 bg-primary/5 border border-primary/20 rounded-xl p-3 mb-4">
        <span className="text-xl">🇰🇪</span>
        <div>
          <p className="text-xs font-700 text-foreground">Sheikh Ibrahim Al-Husseini</p>
          <p className="text-[10px] text-muted-foreground">Fiqh & Ibadah Specialist · Responds in 24–48h</p>
        </div>
        <button className="ml-auto text-xs text-primary font-600 hover:underline">Change</button>
      </div>

      {/* Question input */}
      <div className="relative mb-4">
        <textarea
          value={questionText}
          onChange={handleChange}
          placeholder="e.g. I have difficulty performing wudu due to my arthritis. Is there a valid alternative method I can use for my daily prayers?"
          rows={5}
          className="w-full bg-muted/50 border border-border rounded-xl p-3 text-sm text-foreground placeholder:text-muted-foreground/60 resize-none focus:outline-none focus:border-primary/50 transition-colors leading-relaxed"
        />
        <span className={`absolute bottom-2.5 right-3 text-[10px] ${charCount > MAX_CHARS * 0.9 ? 'text-amber-500' : 'text-muted-foreground/50'}`}>
          {charCount}/{MAX_CHARS}
        </span>
      </div>

      {/* Privacy note */}
      <div className="flex items-start gap-2 bg-muted/30 rounded-xl p-3 mb-5 text-xs text-muted-foreground">
        <span className="text-base">🔐</span>
        <span>Your question is encrypted and only visible to the scholar you select. It will never be shared publicly without your consent.</span>
      </div>

      <button
        onClick={onNext}
        className="w-full py-3 rounded-xl gradient-teal text-white font-700 text-sm shadow-prayer hover:opacity-90 transition-opacity"
      >
        Now choose your scholar →
      </button>
    </div>
  );
}

function SelectScholarStep({ onNext, onScholarSelect }: { onNext: () => void; onScholarSelect: (scholarId: string) => void }) {
  const [selected, setSelected] = useState<string | null>(null);

  const handleSelect = (scholarId: string) => {
    setSelected(scholarId);
    onScholarSelect(scholarId);
  };

  return (
    <div className="flex flex-col">
      <div className="mb-4">
        <p className="text-xs font-600 text-primary uppercase tracking-widest mb-1">Step 3 of 3</p>
        <h3 className="text-xl font-800 text-foreground mb-1">Choose Your Scholar</h3>
        <p className="text-sm text-muted-foreground">Select the scholar whose expertise best matches your question. You can change this before submitting.</p>
      </div>

      <div className="space-y-3 mb-5">
        {DEMO_SCHOLARS.map((scholar) => (
          <button
            key={scholar.id}
            onClick={() => handleSelect(scholar.id)}
            className={`w-full flex items-start gap-3 rounded-xl p-3 border text-left transition-all duration-200 ${
              selected === scholar.id
                ? 'border-primary bg-primary/5 shadow-sm'
                : 'border-border bg-card hover:border-primary/30'
            } ${!scholar.available ? 'opacity-60' : ''}`}
            disabled={!scholar.available}
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-xl flex-shrink-0">
              {scholar.flag}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-700 text-foreground">{scholar.name}</span>
                {!scholar.available && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">Busy</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mb-1">{scholar.title}</p>
              <p className="text-[10px] text-muted-foreground/70">Responds in {scholar.responseTime}</p>
            </div>
            <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
              selected === scholar.id ? 'border-primary bg-primary' : 'border-border'
            }`}>
              {selected === scholar.id && (
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              )}
            </div>
          </button>
        ))}
      </div>

      <button
        onClick={onNext}
        disabled={!selected}
        className={`w-full py-3 rounded-xl font-700 text-sm transition-all duration-200 ${
          selected
            ? 'gradient-teal text-white shadow-prayer hover:opacity-90'
            : 'bg-muted text-muted-foreground cursor-not-allowed'
        }`}
      >
        {selected ? 'Submit my question →' : 'Select a scholar to continue'}
      </button>
    </div>
  );
}

function DoneStep({ onClose, completedAllSteps, lastStepReached }: { onClose: () => void; completedAllSteps: boolean; lastStepReached: WalkthroughStep }) {
  const handleSignup = () => {
    trackSignupFromWalkthrough({
      completed_all_steps: completedAllSteps,
      last_step_reached: lastStepReached,
    });
    onClose();
  };

  return (
    <div className="flex flex-col items-center text-center px-2">
      <div className="w-20 h-20 rounded-2xl gradient-teal flex items-center justify-center text-4xl shadow-prayer mb-6">
        🚀
      </div>
      <h3 className="text-2xl font-800 text-foreground mb-3">You're ready!</h3>
      <p className="text-muted-foreground text-sm leading-relaxed max-w-xs mb-6">
        You've seen how the Scholar Directory works. Create your free account to submit your first question and receive a personal answer from a verified Islamic scholar.
      </p>

      <div className="w-full space-y-3 mb-4">
        <Link
          href="/sign-up-login-screen"
          onClick={handleSignup}
          className="block w-full py-3.5 rounded-xl gradient-teal text-white font-700 text-sm shadow-prayer hover:opacity-90 transition-opacity text-center"
        >
          Create Free Account
        </Link>
        <Link
          href="/seniors/scholars"
          onClick={onClose}
          className="block w-full py-3 rounded-xl bg-card border border-border text-foreground font-600 text-sm hover:bg-muted transition-colors text-center"
        >
          Browse Scholar Directory
        </Link>
      </div>

      <button
        onClick={onClose}
        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        Maybe later
      </button>
    </div>
  );
}

// ─── Progress Bar ─────────────────────────────────────────────────────────────
function StepProgress({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-1.5 mb-6">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={`prog-${i}`}
          className={`h-1 rounded-full transition-all duration-400 ${
            i < current ? 'bg-primary' : i === current ? 'bg-primary/60 flex-[2]' : 'bg-border'
          } ${i === current ? 'flex-[2]' : 'flex-1'}`}
        />
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
interface SeniorsWalkthroughProps {
  onClose: () => void;
}

export default function SeniorsWalkthrough({ onClose }: SeniorsWalkthroughProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef<number>(Date.now());

  const currentStep = STEP_ORDER[stepIndex];
  const isLast = stepIndex === STEP_ORDER.length - 1;
  const totalSteps = STEP_ORDER.length;

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    // Track walkthrough started
    trackWalkthroughStarted({ source: 'tour_button' });
    return () => clearTimeout(t);
  }, []);

  const goNext = useCallback(() => {
    if (isLast) return;
    const nextIndex = stepIndex + 1;
    const nextStep = STEP_ORDER[nextIndex];
    setStepIndex(nextIndex);
    trackWalkthroughStep({
      step_name: nextStep,
      step_index: nextIndex,
      total_steps: totalSteps,
    });
    // Track completion when reaching the done step
    if (nextStep === 'done') {
      const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
      trackWalkthroughCompleted({ total_steps: totalSteps, time_spent_seconds: timeSpent });
    }
  }, [isLast, stepIndex, totalSteps]);

  const handleClose = useCallback(() => {
    setVisible(false);
    // Track abandonment if not on done step
    if (currentStep !== 'done') {
      trackWalkthroughAbandoned({
        abandoned_at_step: currentStep,
        abandoned_at_index: stepIndex,
        total_steps: totalSteps,
        completion_rate: computeCompletionRate(stepIndex, totalSteps),
      });
    }
    setTimeout(onClose, 250);
  }, [onClose, currentStep, stepIndex, totalSteps]);

  const handleScholarClick = useCallback((scholarId: string) => {
    const scholar = DEMO_SCHOLARS.find((s) => s.id === scholarId);
    if (!scholar) return;
    trackScholarClicked({
      scholar_id: scholar.id,
      scholar_name: scholar.name,
      scholar_specialisations: scholar.specialisation,
      scholar_available: scholar.available,
      context: 'walkthrough_directory',
    });
  }, []);

  const handleScholarSelect = useCallback((scholarId: string) => {
    const scholar = DEMO_SCHOLARS.find((s) => s.id === scholarId);
    if (!scholar) return;
    trackScholarSelected({
      scholar_id: scholar.id,
      scholar_name: scholar.name,
      scholar_specialisations: scholar.specialisation,
      step: 'select-scholar',
    });
  }, []);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) handleClose();
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
        transition: 'opacity 0.25s',
        opacity: visible ? 1 : 0,
      }}
    >
      <div
        className="relative w-full max-w-sm bg-background border border-border rounded-3xl shadow-2xl overflow-hidden"
        style={{
          transition: 'transform 0.25s cubic-bezier(0.34,1.56,0.64,1), opacity 0.25s',
          transform: visible ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(12px)',
          opacity: visible ? 1 : 0,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-0">
          <div className="flex items-center gap-2">
            <span className="text-lg">{STEP_META[currentStep].emoji}</span>
            <span className="text-sm font-700 text-foreground">{STEP_META[currentStep].label}</span>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Close walkthrough"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress */}
        <div className="px-5 pt-4">
          <StepProgress current={stepIndex} total={totalSteps} />
        </div>

        {/* Step content */}
        <div className="px-5 pb-6">
          {currentStep === 'intro' && <IntroStep onNext={goNext} />}
          {currentStep === 'directory' && <DirectoryStep onNext={goNext} onScholarClick={handleScholarClick} />}
          {currentStep === 'scholar-profile' && <ScholarProfileStep onNext={goNext} />}
          {currentStep === 'ask-flow' && <AskFlowStep onNext={goNext} />}
          {currentStep === 'select-scholar' && <SelectScholarStep onNext={goNext} onScholarSelect={handleScholarSelect} />}
          {currentStep === 'done' && <DoneStep onClose={handleClose} completedAllSteps={true} lastStepReached={currentStep} />}
        </div>

        {/* Step counter */}
        {currentStep !== 'done' && (
          <div className="px-5 pb-4 text-center">
            <span className="text-xs text-muted-foreground/60">
              {stepIndex + 1} of {totalSteps}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
